import { useMemo } from 'react';
import c from 'classnames';

import useTaxaBarChartData from 'services/microbiome/useTaxaBarChartData';
import { capitalize, formatNumberToDisplayPercentage } from 'helpers';
import { Kit } from '@repo/types';

import { PopHover, Typography } from 'components';

import styles from './MicrobiomeBar.module.scss';

// Comment to trigger a deploy (again)

const getPopHoverTarget = (type: string, value: number, kitId: string) => {
  // Ensure value is treated as a number for calculations, handle potential NaN
  const numericValue = Number(value);
  const safeValue = isNaN(numericValue) ? 0 : numericValue;
  return `${type}_slice_${safeValue
    .toString()
    .replace('.', '')
    .substring(0, 7)}${kitId}`;
};

type BacteriaCategory = {
  key: string;
  name: string;
  color: string;
  value: number;
  order: number;
};

type BacteriaCategories = {
  [key: string]: BacteriaCategory;
};

const categoryColors: { [key: string]: string } = {
  beneficial: 'green',
  protective: 'green',
  unfriendly: 'red',
  disruptive: 'red',
  variable: 'orange',
  unknown: 'grey',
};

const categoryOrders: { [key: string]: number } = {
  unfriendly: 1,
  disruptive: 1,
  beneficial: 2,
  protective: 2,
  variable: 3,
  unknown: 4,
};

export const MicrobiomeBar = ({
  kit,
  displayMode,
}: {
  kit: Kit;
  displayMode?: 'full' | 'card';
}) => {
  const { data } = useTaxaBarChartData(kit.id, kit.sampling_type);

  const bacteriaCategories = useMemo(() => {
    if (!data) return [];

    const categories: BacteriaCategories = {};

    Object.keys(data).forEach(category => {
      const value = data[category as keyof typeof data];
      const numericValue = Number(value);

      if (!isNaN(numericValue)) {
        const categoryKey = category;
        // Backend already returns percentages (0-100), so use directly
        const percentage = numericValue;

        if (categoryKey in categoryColors && categoryKey in categoryOrders) {
          categories[categoryKey] = {
            key: capitalize(categoryKey),
            name: capitalize(categoryKey),
            color: categoryColors[categoryKey],
            value: percentage,
            order: categoryOrders[categoryKey],
          };
        } else {
          categories[categoryKey] = {
            key: capitalize(categoryKey),
            name: capitalize(categoryKey),
            color: 'grey',
            value: percentage,
            order: 99,
          };
        }
      }
    });

    return Object.values(categories)
      .filter(cat => cat.value > 0 || cat.key === 'Unknown')
      .sort((a, b) => a.order - b.order);
  }, [data]);

  return (
    <div className={styles.compositionWrapper}>
      {/* Graph Container */}
      <div className={styles.graphContainer}>
        {bacteriaCategories.map(category => {
          const baseColorClass = styles[category.color] ?? styles.grey;

          return (
            <div
              key={category.name}
              className={c(styles.bar, baseColorClass)}
              style={{
                flexBasis: `${category.value}%`,
              }}
            >
              <PopHover
                target={getPopHoverTarget(
                  category.name,
                  category.value,
                  kit.id,
                )}
                category={category.name}
                className={`pop-hover`}
                placement='top'
              >
                {formatNumberToDisplayPercentage(category.value)}
              </PopHover>
            </div>
          );
        })}
      </div>

      {/* Card Legend (Card Display Mode) */}
      {displayMode === 'card' && (
        <div className='flex flex-wrap gap-1 mt-2'>
          {bacteriaCategories.map(category => (
            <div className='flex align-items-center gap-1' key={category.name}>
              <span
                className={c(
                  styles.circle,
                  styles[category.color] ?? styles.grey,
                )}
              />
              <Typography variant='label'>{category.name}</Typography>
              <Typography
                variant='label'
                className={styles.abundancePercentage}
              >
                {formatNumberToDisplayPercentage(category.value)}
              </Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
